/**
 * Choix du projet destinataire, et bandeau de reconnaissance de format.
 *
 * L'ordre des gestes est ce qui compte ici, et il est l'inverse de l'évident.
 *
 * L'approche évidente demanderait le projet **avant** le dépôt. Elle est simple à écrire
 * et coûte un geste à chaque fichier — or le geste se répète tous les matins, sur des
 * dépôts qui ne varient jamais. C'est exactement la friction que le §1.2 promet
 * d'éliminer.
 *
 * On dépose donc d'abord, et le fichier se reconnaît lui-même. Trois issues, trois
 * discours différents :
 *
 * - **connu** : « Reconnu — Mesure dimensionnelle machine KMC4 ». Le mapping est appliqué,
 *   on le dit, et un bouton permet de changer d'avis. L'utilisateur n'a rien à faire ;
 * - **dérivé** : « Le format de cette machine a changé depuis le 14 août ». Le projet ne
 *   bouge pas, la série continue, et ce qui a changé est listé en clair — « la colonne
 *   Temperature est apparue » se corrige, « score 0,72 » ne se corrige pas ;
 * - **inconnu** : on demande un nom. C'est le seul cas où l'on interrompt, et il ne se
 *   produit qu'une fois par machine.
 *
 * Reconnaître n'est pas déposer. La reconnaissance **propose** une destination — elle la
 * pré-sélectionne, ce qui laisse le geste quotidien à un seul clic — mais l'écriture
 * attend « Déposer dans ce projet ». Auparavant le simple choix dans la liste écrivait :
 * l'utilisateur n'avait ni bouton à viser, ni confirmation à lire, et repartait sans
 * savoir si ses mesures étaient enregistrées. Un dépôt est irréversible dans les faits —
 * ses caractéristiques appartiennent au projet, ses mesures aux caractéristiques — donc
 * il se demande explicitement et se confirme explicitement.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Check, FolderPlus, Pencil, Save } from "lucide-react";

import type { Recognition } from "./mappingApi";
import { frenchDate, type Project } from "./projectsApi";
import { Badge, Button, Card, Input, Select, cn, num } from "./ui/primitives";

export interface ProjectChoice {
  projectId: string | null;
  projectName: string | null;
}

/** Message du bandeau, dans les mots de l'utilisateur et non ceux du serveur.
 *
 *  Quand le projet retenu **est** celui que la reconnaissance a trouvé, c'est le discours
 *  de reconnaissance qui prime : « Format reconnu » explique pourquoi cette destination
 *  est déjà cochée, là où « Ce dépôt ira dans X » laisserait croire à un choix qu'on
 *  aurait fait soi-même et oublié. */
function verdict(recognition: Recognition | null, chosen: Project | undefined) {
  const reconnu = Boolean(
    chosen && recognition && recognition.project_id === chosen.id,
  );

  if (chosen && !reconnu) {
    return {
      tone: "accent" as const,
      title: `Ce dépôt ira dans « ${chosen.name} »`,
      detail:
        chosen.files_count > 0
          ? `${num(chosen.files_count)} dépôt(s) déjà accumulés, dernier le ${frenchDate(
              chosen.last_deposit_at,
            )}.`
          : "Premier dépôt de ce projet.",
    };
  }

  if (recognition?.outcome === "connu") {
    return {
      tone: "good" as const,
      title: `Format reconnu — « ${recognition.project_name} »`,
      detail: recognition.applied
        ? "Le mapping validé pour ce projet a été appliqué. Rien à reconfirmer."
        : "Ce format est déjà connu de ce projet.",
    };
  }

  if (recognition?.outcome === "derive") {
    return {
      tone: "warn" as const,
      title: recognition.known_since
        ? `Le format de « ${recognition.project_name} » a changé depuis le ${frenchDate(
            recognition.known_since,
          )}`
        : `Le format de « ${recognition.project_name} » a changé`,
      detail:
        "La série continue dans le même projet : une nouvelle version du mapping sera " +
        "enregistrée. Vérifiez les rôles de colonnes ci-dessous.",
    };
  }

  return {
    tone: "neutral" as const,
    title: "Format inconnu",
    detail:
      "Nommez le projet qui recevra ces mesures — « Mesure dimensionnelle machine " +
      "KMC4 », par exemple. Les dépôts suivants s'y accumuleront tout seuls.",
  };
}

export function ProjectPicker({
  projects,
  recognition,
  choice,
  onChange,
  onDeposit,
  deposited,
  measurements,
  busy,
  loading,
}: {
  projects: Project[];
  recognition: Recognition | null;
  choice: ProjectChoice;
  onChange: (next: ProjectChoice) => void;
  /** Demande l'écriture. La destination est passée en argument plutôt que relue dans
   *  `choice` : un nom tapé et non encore validé par la perte de focus ne serait pas
   *  remonté au moment du clic, et le dépôt partirait sans destination. */
  onDeposit: (target: ProjectChoice) => void;
  /** L'analyse affichée a déjà été écrite : le bouton n'a plus rien à demander. */
  deposited: boolean;
  /** Mesures exploitables dans le fichier. À zéro, il n'y a rien à déposer et le dire
   *  vaut mieux qu'un bouton qui ne produit rien. */
  measurements: number;
  busy: boolean;
  loading: boolean;
}) {
  const chosen = projects.find((p) => p.id === choice.projectId);
  const inconnu =
    !chosen &&
    recognition?.outcome !== "connu" &&
    recognition?.outcome !== "derive";

  // Le champ de saisie n'apparaît que s'il sert à quelque chose : format inconnu, ou
  // demande explicite. Un formulaire de création affiché en permanence pousse à créer un
  // projet de plus à chaque dépôt, et l'usine se retrouve avec quarante projets d'une
  // mesure chacun.
  const [naming, setNaming] = useState(inconnu);
  const [draft, setDraft] = useState(choice.projectName ?? "");

  useEffect(() => {
    if (inconnu) setNaming(true);
  }, [inconnu]);

  // Destination du dépôt, telle qu'elle sera envoyée. Le nom en cours de saisie compte
  // autant qu'un projet sélectionné : sans cela, taper un nom puis cliquer directement
  // sur le bouton — ce que tout le monde fait — ne désignerait rien.
  const saisi = draft.trim();
  const destination: ProjectChoice | null = choice.projectId
    ? { projectId: choice.projectId, projectName: null }
    : saisi
      ? { projectId: null, projectName: saisi }
      : choice.projectName?.trim()
        ? { projectId: null, projectName: choice.projectName.trim() }
        : null;

  const état = verdict(recognition, chosen);
  const tones = {
    good: "border-good/40 bg-good-soft",
    warn: "border-warn/40 bg-warn-soft",
    accent: "border-accent/40 bg-accent-soft",
    neutral: "border-line bg-surface-2",
  };

  return (
    <Card
      className={cn(
        "animate-[rise_0.35s_cubic-bezier(0.16,1,0.3,1)_both] overflow-hidden",
        tones[état.tone],
      )}
    >
      <div className="flex flex-wrap items-start gap-3 px-4 py-3">
        <span className="mt-0.5 shrink-0">
          {état.tone === "good" ? (
            <Check className="h-4 w-4 text-good" />
          ) : état.tone === "warn" ? (
            <AlertTriangle className="h-4 w-4 text-warn" />
          ) : (
            <FolderPlus className="h-4 w-4 text-muted" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="text-[0.88rem] font-semibold tracking-tight">
            {état.title}
          </div>
          <p className="mt-0.5 text-[0.8rem] text-muted">{état.detail}</p>

          {recognition?.differences.length ? (
            <ul className="mt-1.5 space-y-0.5">
              {recognition.differences.map((d) => (
                <li key={d} className="text-[0.76rem] text-warn">
                  · {d}
                </li>
              ))}
            </ul>
          ) : null}

          {/* --- Changer de destination --- */}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {projects.length > 0 && (
              <Select
                // Le projet reconnu arrive ici par `choice`, pré-sélectionné en amont.
                // Le relire aussi de `recognition` ferait revenir la destination que
                // l'utilisateur vient d'écarter en repassant sur l'entrée vide.
                value={choice.projectId ?? ""}
                disabled={busy || loading}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "__nouveau__") {
                    setNaming(true);
                    onChange({ projectId: null, projectName: draft || null });
                    return;
                  }
                  setNaming(false);
                  onChange({ projectId: value || null, projectName: null });
                }}
                className="max-w-[18rem]"
              >
                {/* L'entrée vide ne délègue plus rien : elle signifie « aucune
                    destination », et le bouton de dépôt reste alors fermé. Elle
                    s'intitulait « laisser la reconnaissance décider », ce qui décrivait
                    exactement le comportement qu'on vient de retirer. */}
                <option value="">choisir un projet…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.measurements_count > 0
                      ? ` — ${num(p.measurements_count)} mesures`
                      : ""}
                  </option>
                ))}
                <option value="__nouveau__">＋ nouveau projet…</option>
              </Select>
            )}

            {!naming && projects.length === 0 && (
              <Button onClick={() => setNaming(true)} disabled={busy}>
                <FolderPlus className="h-3.5 w-3.5" />
                Nommer un projet
              </Button>
            )}

            {naming && (
              <span className="flex items-center gap-1.5">
                <Input
                  autoFocus
                  value={draft}
                  placeholder="Mesure dimensionnelle machine KMC4"
                  className="w-72"
                  disabled={busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && draft.trim()) {
                      onChange({ projectId: null, projectName: draft.trim() });
                    }
                  }}
                  onBlur={() => {
                    const value = draft.trim();
                    onChange({
                      projectId: null,
                      projectName: value ? value : null,
                    });
                  }}
                />
                <Pencil className="h-3.5 w-3.5 text-faint" />
              </span>
            )}

            {recognition && recognition.outcome !== "inconnu" && (
              <Badge tone="neutral">
                ressemblance {Math.round(recognition.score * 100)} %
                {recognition.template_version
                  ? ` · mapping v${recognition.template_version}`
                  : ""}
              </Badge>
            )}
          </div>

          {/* --- Le dépôt lui-même --- */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line/60 pt-3">
            <Button
              variant="primary"
              size="md"
              disabled={
                busy || deposited || destination === null || measurements === 0
              }
              onClick={() => destination && onDeposit(destination)}
            >
              <Save className="h-4 w-4" />
              {deposited ? "Déposé" : "Déposer dans ce projet"}
            </Button>

            <span className="text-[0.78rem] text-muted">
              {deposited
                ? "Corriger le mapping ci-dessous rouvrira le dépôt."
                : measurements === 0
                  ? "Aucune mesure exploitable : il n'y a rien à déposer."
                  : destination === null
                    ? "Choisissez ou nommez d'abord le projet destinataire."
                    : "Rien n'est encore enregistré : l'analyse ci-dessous est un aperçu."}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
